<script setup lang="ts">
import VerticalBar from './VerticalBar.vue'
import Capsule from '@/components/common/Capsule.vue'
import DollarValue from '@/components/common/DollarValue.vue'
import DataTable from './DataTable.vue'
import type { PharmaProgramsProduct } from '@/data'
import {useThemeStore, useValueDataStore} from '@/stores'
import { OutlinedDownIcon } from '@/components/icons'
import { ref } from 'vue'
import {storeToRefs} from "pinia";

interface IProps {
  isMain: boolean
  data: PharmaProgramsProduct
  color: string
  chartMaxHeight?: number
}

type OtherProgram = {
  logo: any
  claimsEarnedValue: number
  claimsOpportunityValue: number
  width?: string
  height?: string
}

const props = defineProps<IProps>()

const valueStore = useValueDataStore();
const {valuePeriods} = storeToRefs(valueStore);

const themeStore = useThemeStore()
const { colorVidacorp, colorBackgroundBright } = themeStore.themeColors

const showDetails = ref(false)
const enableAnimation = ref(false)

function handleClick() {
  showDetails.value = true
  enableAnimation.value = true
}

function handleBack() {
  showDetails.value = false
  setTimeout(() => {
    enableAnimation.value = false
  }, 300) // Match the duration of the slide animation
}
</script>

<template>
  <Capsule
    :isMain="false"
    mode="static"
    :childrenCount="1"
    :background="props.isMain ? 'dark' : 'light'"
    :pharmaProgrmas="true"
    :glow="props.isMain"
  >
    <template #1>
      <div
        class="d-flex flex-column align-items-center justify-content-center"
        style="height: 100%; width: 100%"
        :class="{
          slideUp: showDetails && enableAnimation,
          slideDown: !showDetails && enableAnimation
        }"
      >
        <div class="main-wrapper">
          <slot name="logo" />
          <div>
            <div :class="{ main: props.isMain }">
              <span class="sub-title">Earned Value</span>
              <DollarValue
                :value="data.claimsEarnedValue"
                fontSize="1.375rem"
                :color="colorVidacorp"
                :privacy="true"
              >
                <template #info>
                  Total value earned for {{ data.brandName }} claims from {{ valuePeriods?.earnedValueStartMonth }} - {{ valuePeriods?.earnedValueEndMonth }}.
                </template>
              </DollarValue>
              <br />
            </div>
            <div class="claims-opportunity" :class="{ main: props.isMain }">
              <span class="title">Claims Opportunity</span>
              <DollarValue
                :value="data.claimsOpportunityValue"
                fontSize="2.1875rem"
                :privacy="false"
              >
                <template #info>
                  Total missed opportunity on {{ data.brandName }} claims from {{valuePeriods?.opportunityStartMonth}} - {{valuePeriods?.opportunityMonth}}. It is the value of missed claims times average professional service fee (${{ data.averageFee }})
                </template>
              </DollarValue>
            </div>
          </div>
          <DataTable
            :scripts="data.scripts"
            :claims="data.claims"
            :scriptsVersusPreviousQuarter="data.scriptsVersusPreviousQuarter"
            :claimsVersusPreviousQuarter="data.claimsVersusPreviousQuarter"
            :isMain="props.isMain"
          />
          <div class="chart-container">
            <VerticalBar
              :data="props.data.scriptsAndClaimsSeries"
              :maxHeight="props.chartMaxHeight || 5000"
              :primaryColor="props.color"
              :gridLineCount="6"
              :isMain="props.isMain"
              chartTitle="Scripts vs Claims"
            />
            <div @click="handleClick" class="caption" :class="{ hide: !props.isMain }">
              <OutlinedDownIcon /><span>More details</span>
            </div>
          </div>
        </div>
        <div class="other-programs-wrapper">
          <button class="caption" @click="handleBack">Back</button>
          <div
            class="other-program-item"
            v-for="(item, index) in props.data.breakdownSeries as OtherProgram"
            :key="index"
          >
            <component
              :is="item.logo"
              :width="item.width"
              :height="item.height"
              :fill="colorBackgroundBright"
            />
            <div class="other-program-item-values">
              <div>
                <span class="sub-title">Earned Value</span>
                <DollarValue
                  :value="item.claimsEarnedValue"
                  fontSize="22px"
                  :color="colorVidacorp"
                  :privacy="true"
                />
              </div>
              <div>
                <span class="sub-title">Claims Opportunity</span>
                <DollarValue
                  :value="item.claimsOpportunityValue"
                  fontSize="22px"
                  :color="colorVidacorp"
                  :privacy="true"
                >
              <template #info>
                  Average professional service fee = ${{ item.averageFee }}
                </template>
              </DollarValue>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Capsule>
</template>

<style scoped>
.main-wrapper,
.other-programs-wrapper {
  width: 100%;
  height: 100%;
  padding: 10%;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-around;
}

.other-programs-wrapper {
  justify-content: flex-start;
}

.other-program-item {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding-block: 10px;
  gap: 10px;
  border-bottom: 1px solid var(--color-background-bright-80);
}

.other-program-item-values {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.other-program-item-values > div {
  width: 50%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}

.other-program-item-values > div:first-child {
  padding-inline: 1rem;
}

.chart-container {
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* gap: 1rem; */
}

.hide {
  visibility: hidden;
}

.title {
  font-size: var(--font-size-h2);
  font-weight: 600;
  padding-bottom: 5%;
}
.title.expanded {
  padding-bottom: 0%;
}

.sub-title {
  font-size: var(--font-size-h3);
  font-weight: 600;
}

.claims-opportunity {
  margin-bottom: 10px;
}

.claims-opportunity.main {
  margin-bottom: 0;
}

.caption {
  font-size: var(--font-size-caption);
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  color: var(--color-background-bright);
}

.slideUp {
  transform: translateY(-100%);
  transition: transform 0.3s ease-in-out;
}
.slideDown {
  transform: translateY(0%);
  transition: transform 0.3s ease-in-out;
}
</style>
